import React, { useMemo, useCallback } from "react";
import { Box, Skeleton, Typography, Card } from "@mui/material";
import ChartCard from "@/components/data-visuals/ChartCard";
import { DOCLINK_COLORS } from "@/config/colors";
import {
  buildHubMedOverviewPerDayStackRows,
  HUBMED_OVERVIEW_CHART_SERIES_KEYS,
  HUBMED_OVERVIEW_DAILY_STACK_COLORS,
  hubMedConsultLabel,
  hubMedConsultCount,
} from "./hubMedChartHelpers";

const dayBookingDateRaw = (d) => d?.bookingDate ?? d?.BookingDate;

const dayTotalAppts = (d) =>
  Number(d?.totalAppointments ?? d?.TotalAppointments ?? 0);

const dayBreakdown = (d) =>
  d?.consultTypeBreakdown ?? d?.ConsultTypeBreakdown ?? [];

const dayKeyFromPoint = (x) => {
  if (x instanceof Date && !Number.isNaN(x.getTime())) {
    return x.toISOString().split("T")[0];
  }
  if (x == null) return "";
  const s = String(x);
  return s.includes("T") ? s.split("T")[0] : s.slice(0, 10);
};

const HubMedAppointmentsChart = ({
  appointmentsPerDay,
  loading,
  visible,
  mainChartMinHeight = 380,
}) => {
  const dayMap = useMemo(() => {
    const m = new Map();
    (appointmentsPerDay || []).forEach((d) => {
      const raw = dayBookingDateRaw(d);
      const key =
        typeof raw === "string"
          ? raw.split("T")[0]
          : raw instanceof Date
            ? raw.toISOString().split("T")[0]
            : String(raw || "");
      if (key) m.set(key, d);
    });
    return m;
  }, [appointmentsPerDay]);

  const chartRows = useMemo(
    () => buildHubMedOverviewPerDayStackRows(appointmentsPerDay),
    [appointmentsPerDay]
  );

  const stackedByDayKey = useMemo(() => {
    const m = new Map();
    chartRows.forEach((row) => {
      const raw = row?.BookingDate;
      const key =
        typeof raw === "string"
          ? raw.split("T")[0]
          : raw instanceof Date
            ? raw.toISOString().split("T")[0]
            : String(raw || "");
      if (key) m.set(key, row);
    });
    return m;
  }, [chartRows]);

  const tooltipRender = useCallback(
    (args) => {
      const xVal = args.point?.x ?? args.series?.points?.[args.point?.index]?.x;
      const key = dayKeyFromPoint(xVal);
      const day = dayMap.get(key);
      const stackRow = stackedByDayKey.get(key);
      if (!day && !stackRow) return;

      const lines = [];
      if (day) {
        lines.push(
          `<b>Total appointments:</b> ${dayTotalAppts(day).toLocaleString()}`
        );
        const br = dayBreakdown(day).filter((c) => hubMedConsultCount(c) > 0);
        if (br.length) {
          lines.push("<b>Types:</b>");
          br.forEach((c) => {
            lines.push(
              `${hubMedConsultLabel(c)}: ${hubMedConsultCount(c).toLocaleString()}`
            );
          });
        }
      }
      if (stackRow) {
        lines.push("<b>By category:</b>");
        HUBMED_OVERVIEW_CHART_SERIES_KEYS.forEach((k) => {
          const n = Number(stackRow[k] ?? 0);
          if (n > 0) lines.push(`${k}: ${n.toLocaleString()}`);
        });
      }
      args.text = lines.join("<br/>");
    },
    [dayMap, stackedByDayKey]
  );

  const series = useMemo(() => {
    if (!chartRows.length) return [];
    return HUBMED_OVERVIEW_CHART_SERIES_KEYS.map((name, i) => ({
      dataSource: chartRows,
      xName: "BookingDate",
      yName: name,
      type: "StackingColumn",
      name,
      fill: HUBMED_OVERVIEW_DAILY_STACK_COLORS[i] ?? "#64748b",
      columnSpacing: 0.35,
      cornerRadius: { topLeft: 3, topRight: 3 },
    }));
  }, [chartRows]);

  const totalAppointmentsInRange = useMemo(
    () =>
      (appointmentsPerDay || []).reduce((s, d) => s + dayTotalAppts(d), 0),
    [appointmentsPerDay]
  );

  const totalStackedInRange = useMemo(
    () =>
      chartRows.reduce(
        (acc, row) =>
          acc +
          HUBMED_OVERVIEW_CHART_SERIES_KEYS.reduce(
            (s, k) => s + Number(row[k] ?? 0),
            0
          ),
        0
      ),
    [chartRows]
  );

  if (!visible) return null;

  if (loading) {
    return (
      <Skeleton variant="rounded" height={400} sx={{ borderRadius: 2 }} />
    );
  }

  if (!chartRows.length || totalAppointmentsInRange === 0) {
    return (
      <Card variant="outlined" sx={{ borderColor: DOCLINK_COLORS.LIGHT_PURPLE }}>
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            No HubMed appointments found for selected range.
          </Typography>
        </Box>
      </Card>
    );
  }

  if (totalStackedInRange === 0) {
    return (
      <Card variant="outlined" sx={{ borderColor: DOCLINK_COLORS.LIGHT_PURPLE }}>
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            Appointments exist in this range, but none matched the chart
            categories. If the API uses different consult names, extend{" "}
            <code>classifyHubMedOverviewChartBucket</code> in{" "}
            <code>hubMedChartHelpers.js</code> or combine types in the API.
          </Typography>
        </Box>
      </Card>
    );
  }

  const chartData = { HubMedPerDay: chartRows };

  return (
    <Box sx={{ "& > div": { mt: 0 } }}>
      <ChartCard
        title="Appointments per day"
        hidePieChart
        mainChartMinHeight={mainChartMinHeight}
        data={chartData}
        primaryXAxis={{
          valueType: "DateTime",
          title: "Date",
          labelFormat: "dd/MM",
          intervalType: "Days",
        }}
        primaryYAxis={{
          title: "Appointments",
          labelFormat: "{value}",
        }}
        borderClass="border-4 border-purple-light"
        series={series}
        pieData={[]}
        mainChartTooltipRender={tooltipRender}
      />
    </Box>
  );
};

export default HubMedAppointmentsChart;
